import React from 'react';
import { RefreshCw, FileSpreadsheet, FileText, FileDown } from 'lucide-react';
import styles from './AnalyticsFiltersBar.module.scss';
import { AdminAnalyticsFilters } from '../../../../types/adminAnalytics.ts';
import { toDateInputValue, fromDateInputValue } from '../../../../utils/adminAnalyticsHelpers.ts';

type DatePreset = 'last7' | 'last30' | 'thisMonth' | 'lastMonth';

interface AnalyticsFiltersBarProps {
  filters: AdminAnalyticsFilters;
  loading: boolean;
  exportLoading: boolean;
  onFiltersChange: (partial: Partial<AdminAnalyticsFilters>) => void;
  onApply: () => void;
  onPreset: (preset: DatePreset) => void;
  onExportXlsx: () => void;
  onExportCsv: () => void;
  onExportPdf: () => void;
}

const AnalyticsFiltersBar: React.FC<AnalyticsFiltersBarProps> = ({
  filters,
  loading,
  exportLoading,
  onFiltersChange,
  onApply,
  onPreset,
  onExportXlsx,
  onExportCsv,
  onExportPdf,
}) => {
  return (
    <div className={styles.bar}>
      <div className={styles.row}>
        <div className={styles.presets}>
          <button type="button" className={styles.presetBtn} onClick={() => onPreset('last7')}>
            Últimos 7 días
          </button>
          <button type="button" className={styles.presetBtn} onClick={() => onPreset('last30')}>
            Últimos 30 días
          </button>
          <button type="button" className={styles.presetBtn} onClick={() => onPreset('thisMonth')}>
            Mes actual
          </button>
          <button type="button" className={styles.presetBtn} onClick={() => onPreset('lastMonth')}>
            Mes anterior
          </button>
        </div>
      </div>

      <div className={styles.row}>
        <label className={styles.field}>
          <span>Desde</span>
          <input
            type="date"
            value={toDateInputValue(filters.from)}
            onChange={(e) =>
              onFiltersChange({
                from: e.target.value ? fromDateInputValue(e.target.value, false) : filters.from,
              })
            }
          />
        </label>
        <label className={styles.field}>
          <span>Hasta</span>
          <input
            type="date"
            value={toDateInputValue(filters.to)}
            onChange={(e) =>
              onFiltersChange({
                to: e.target.value ? fromDateInputValue(e.target.value, true) : filters.to,
              })
            }
          />
        </label>

        <label className={styles.checkbox}>
          <input
            type="checkbox"
            checked={filters.includeDeleted}
            onChange={(e) => onFiltersChange({ includeDeleted: e.target.checked })}
          />
          Incluir eliminados
        </label>
        <label className={styles.checkbox}>
          <input
            type="checkbox"
            checked={filters.usersValidatedOnly}
            onChange={(e) => onFiltersChange({ usersValidatedOnly: e.target.checked })}
          />
          Solo usuarios validados
        </label>

        <button
          type="button"
          className={styles.primaryBtn}
          onClick={onApply}
          disabled={loading}
        >
          <RefreshCw size={16} className={loading ? styles.spin : undefined} />
          Actualizar
        </button>
      </div>

      <div className={styles.row}>
        <label className={styles.checkbox}>
          <input
            type="checkbox"
            checked={filters.useExplicitCompare}
            onChange={(e) => onFiltersChange({ useExplicitCompare: e.target.checked })}
          />
          Comparación personalizada
        </label>
        {filters.useExplicitCompare && (
          <>
            <label className={styles.field}>
              <span>Comparar desde</span>
              <input
                type="date"
                value={filters.compareFrom ? toDateInputValue(filters.compareFrom) : ''}
                onChange={(e) =>
                  onFiltersChange({
                    compareFrom: e.target.value
                      ? fromDateInputValue(e.target.value, false)
                      : null,
                  })
                }
              />
            </label>
            <label className={styles.field}>
              <span>Comparar hasta</span>
              <input
                type="date"
                value={filters.compareTo ? toDateInputValue(filters.compareTo) : ''}
                onChange={(e) =>
                  onFiltersChange({
                    compareTo: e.target.value
                      ? fromDateInputValue(e.target.value, true)
                      : null,
                  })
                }
              />
            </label>
          </>
        )}
      </div>

      <div className={styles.exportRow}>
        <span className={styles.exportLabel}>Exportar informe</span>
        <span className={styles.exportHint}>Los gráficos se incluyen en el PDF</span>
        <button
          type="button"
          className={styles.exportBtn}
          onClick={onExportXlsx}
          disabled={exportLoading || loading}
        >
          <FileSpreadsheet size={16} />
          Excel
        </button>
        <button
          type="button"
          className={styles.exportBtn}
          onClick={onExportCsv}
          disabled={exportLoading || loading}
        >
          <FileText size={16} />
          CSV
        </button>
        <button
          type="button"
          className={styles.exportBtn}
          onClick={onExportPdf}
          disabled={exportLoading || loading}
        >
          <FileDown size={16} />
          PDF con gráficos
        </button>
      </div>
    </div>
  );
};

export type { DatePreset };
export default AnalyticsFiltersBar;
